import { useContext } from 'react';
import { CheckCircleIcon, PlusIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from 'react-tooltip';
import { T, TranslationsContext, translation } from '../../../../../../../../../Translations';
import { QuickAddGroup, categoryLabel } from './Groups';

interface Props {
  group: QuickAddGroup;
  isDone: boolean;
  // False for the button just clicked, until the pointer or focus leaves it, so a cursor
  // still on it doesn't immediately preview the opposite action (red remove, teal add)
  hoverable: boolean;
  onClick: () => void;
  onLeave: () => void;
}

// Theme colors are plain var() values, so Tailwind opacity modifiers on them (bg-primary/10)
// compile to no CSS (DVLP-8193). Tints mix the token with transparent instead.
const ADD_CLASSES =
  'border-[color-mix(in_srgb,var(--primary)_40%,transparent)] text-primary bg-card';
const ADD_HOVER_CLASSES = 'hover:bg-primary hover:text-primary-foreground hover:border-primary';
const DONE_CLASSES = 'border-border text-muted-foreground bg-transparent';
const DONE_HOVER_CLASSES =
  'hover:border-destructive hover:text-destructive hover:bg-[color-mix(in_srgb,var(--destructive)_5%,transparent)]';
const ADD_COUNT_CLASSES = 'bg-[color-mix(in_srgb,var(--primary)_10%,transparent)]';
const ADD_COUNT_HOVER_CLASSES =
  'group-hover:bg-[color-mix(in_srgb,var(--primary-foreground)_20%,transparent)]';
const DONE_COUNT_CLASSES = 'bg-muted';
const DONE_COUNT_HOVER_CLASSES =
  'group-hover:bg-[color-mix(in_srgb,var(--destructive)_10%,transparent)]';

function QuickAddButton({ group, isDone, hoverable, onClick, onLeave }: Props) {
  const ts = useContext(TranslationsContext);
  const tooltipId = `quick-add-${group.key}`;
  const descriptionId = `quick-add-${group.key}-description`;
  const count = group.courseIds.length;

  const label = (): string => {
    switch (group.kind) {
      case 'all':
        return translation('All')(ts);
      case 'category':
        return translation(categoryLabel(group.key))(ts);
    }
  };

  const tooltip = () => {
    switch (group.kind) {
      case 'all':
        return isDone ? (
          <T kind="Remove all {{count}} courses from this section" count={count} />
        ) : (
          <T kind="Include all {{count}} courses in this section" count={count} />
        );
      case 'category':
        return isDone ? (
          <T
            kind="Remove all {{count}} {{courseType}} courses from this section"
            count={count}
            courseType={label()}
          />
        ) : (
          <T
            kind="Include all {{count}} {{courseType}} courses in this section"
            count={count}
            courseType={label()}
          />
        );
    }
  };

  const stateClasses = isDone
    ? `${DONE_CLASSES} ${hoverable ? DONE_HOVER_CLASSES : ''}`
    : `${ADD_CLASSES} ${hoverable ? ADD_HOVER_CLASSES : ''}`;

  const countClasses = isDone
    ? `${DONE_COUNT_CLASSES} ${hoverable ? DONE_COUNT_HOVER_CLASSES : ''}`
    : `${ADD_COUNT_CLASSES} ${hoverable ? ADD_COUNT_HOVER_CLASSES : ''}`;

  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        onMouseLeave={onLeave}
        onBlur={onLeave}
        className={`group inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 border rounded-md whitespace-nowrap transition-colors ${stateClasses}`}
        style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
        aria-pressed={isDone}
        aria-describedby={descriptionId}
        data-intercom-target={`builder-v2-quick-add-${group.key}-button`}
        data-tooltip-id={tooltipId}
      >
        {isDone ? (
          <>
            <CheckCircleIcon
              className={`w-4 h-4 text-accent ${hoverable ? 'group-hover:hidden' : ''}`}
            />
            {hoverable && (
              <XCircleIcon className="w-4 h-4 text-destructive hidden group-hover:block" />
            )}
          </>
        ) : (
          <PlusIcon className="w-4 h-4" />
        )}
        <span>{label()}</span>
        <span
          className={`px-1.5 rounded-full ${countClasses}`}
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-semibold)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count}
        </span>
      </button>
      {/* The tooltip is hidden right after a click; this description always names the action */}
      <span id={descriptionId} className="sr-only">
        {tooltip()}
      </span>
      {hoverable && (
        <Tooltip
          id={tooltipId}
          place="bottom"
          opacity={1}
          style={{
            backgroundColor: 'var(--foreground)',
            borderRadius: '8px',
            padding: '8px 12px',
            zIndex: 1000,
          }}
        >
          <p className="text-background" style={{ fontSize: 'var(--text-xs)' }}>
            {tooltip()}
          </p>
        </Tooltip>
      )}
    </div>
  );
}

export default QuickAddButton;
